import React, { useState, useRef, useEffect } from "react";
import "./DateRangePicker.scss";
import { ButtonRound } from "@bms-ai/bms-ai-ui-lib/components";
import ChevronLeftIcon from "../../_assets/icons/ChevronLeftIcon";
import ChevronRightIcon from "../../_assets/icons/ChevronRightIcon";
import { formatDate } from "../../helpers/dateUtils";

interface DateRangePickerProps {
  startDate: Date | null;
  endDate: Date | null;
  onChange: (startDate: Date | null, endDate: Date | null) => void;
}

export const DateRangePicker: React.FC<DateRangePickerProps> = ({
  startDate,
  endDate,
  onChange,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [tempStartDate, setTempStartDate] = useState<Date | null>(startDate);
  const [tempEndDate, setTempEndDate] = useState<Date | null>(endDate);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const pickerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        pickerRef.current &&
        !pickerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const getDateRangeText = () => {
    if (!startDate && !endDate) return "Select date range";
    if (startDate && !endDate) return formatDate(startDate);
    if (startDate && endDate)
      return `${formatDate(startDate)}-${formatDate(endDate)}`;
    return "";
  };

  const handleDateClick = (date: Date) => {
    if (!tempStartDate || (tempStartDate && tempEndDate)) {
      setTempStartDate(date);
      setTempEndDate(null);
    } else if (tempStartDate && !tempEndDate) {
      if (date < tempStartDate) {
        setTempStartDate(date);
        setTempEndDate(tempStartDate);
      } else {
        setTempEndDate(date);
      }
    }
  };

  const handleConfirm = () => {
    onChange(tempStartDate, tempEndDate);
    setIsOpen(false);
  };

  const handleClear = () => {
    setTempStartDate(null);
    setTempEndDate(null);
    onChange(null, null);
  };

  const isDateInRange = (date: Date) => {
    if (!tempStartDate || !tempEndDate) return false;
    return date >= tempStartDate && date <= tempEndDate;
  };

  const isDateSelected = (date: Date) => {
    return (
      (tempStartDate && isSameDay(date, tempStartDate)) ||
      (tempEndDate && isSameDay(date, tempEndDate))
    );
  };

  const isSameDay = (date1: Date, date2: Date) => {
    return (
      date1.getFullYear() === date2.getFullYear() &&
      date1.getMonth() === date2.getMonth() &&
      date1.getDate() === date2.getDate()
    );
  };

  const renderCalendar = (monthOffset: number) => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth() + monthOffset;
    const displayDate = new Date(year, month, 1);

    const firstDay = new Date(displayDate.getFullYear(), displayDate.getMonth(), 1);
    const lastDay = new Date(
      displayDate.getFullYear(),
      displayDate.getMonth() + 1,
      0
    );

    const startingDayOfWeek = firstDay.getDay();
    const daysInMonth = lastDay.getDate();

    const monthName = displayDate.toLocaleString("default", {
      month: "long",
      year: "numeric",
    });

    const days: (number | null)[] = [];
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null);
    }
    for (let i = 1; i <= daysInMonth; i++) {
      days.push(i);
    }

    const today = new Date();
    today.setHours(0, 0, 0, 0); // Reset time to start of day for accurate comparison

    return (
      <div className="date-range-calendar">
        <div className="calendar-header">
          {monthOffset === 0 && (
            <button
              className="nav-button"
              onClick={() =>
                setCurrentMonth(
                  new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1)
                )
              }
            >
              <ChevronLeftIcon />
            </button>
          )}
          <span className="month-name">{monthName}</span>
          {monthOffset === 1 && (
            <button
              className="nav-button"
              onClick={() =>
                setCurrentMonth(
                  new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1)
                )
              }
            >
              <ChevronRightIcon />
            </button>
          )}
        </div>
        <div className="calendar-weekdays">
          {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((day) => (
            <div key={day} className="weekday">
              {day}
            </div>
          ))}
        </div>
        <div className="calendar-days">
          {days.map((day, index) => {
            if (day === null) {
              return <div key={`empty-${index}`} className="calendar-day empty" />;
            }

            const date = new Date(
              displayDate.getFullYear(),
              displayDate.getMonth(),
              day
            );
            date.setHours(0, 0, 0, 0); // Reset time for accurate comparison

            const isInRange = isDateInRange(date);
            const isSelected = isDateSelected(date);
            const isToday = isSameDay(date, new Date());
            const isFutureDate = date > today;

            return (
              <button
                key={day}
                className={`calendar-day ${isInRange ? "in-range" : ""} ${
                  isSelected ? "selected" : ""
                } ${isToday ? "today" : ""} ${isFutureDate ? "disabled" : ""}`}
                onClick={() => !isFutureDate && handleDateClick(date)}
                disabled={isFutureDate}
              >
                {day}
              </button>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <div className="date-range-picker" ref={pickerRef}>
      <button className="date-input" onClick={() => setIsOpen(!isOpen)}>
        <span className="date-text">{getDateRangeText()}</span>
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M13.333 2.00033H12.6663V1.33366C12.6663 0.966992 12.3663 0.666992 11.9997 0.666992C11.633 0.666992 11.333 0.966992 11.333 1.33366V2.00033H4.66634V1.33366C4.66634 0.966992 4.36634 0.666992 3.99967 0.666992C3.63301 0.666992 3.33301 0.966992 3.33301 1.33366V2.00033H2.66634C1.93301 2.00033 1.33301 2.60033 1.33301 3.33366V14.0003C1.33301 14.7337 1.93301 15.3337 2.66634 15.3337H13.333C14.0663 15.3337 14.6663 14.7337 14.6663 14.0003V3.33366C14.6663 2.60033 14.0663 2.00033 13.333 2.00033ZM12.6663 14.0003H3.33301C2.96634 14.0003 2.66634 13.7003 2.66634 13.3337V5.33366H13.333V13.3337C13.333 13.7003 13.033 14.0003 12.6663 14.0003Z" fill="#BE2BBB"/>
        </svg>
      </button>

      {isOpen && (
        <div className="date-picker-popup">
          <div className="calendars-container">
            {renderCalendar(0)}
            {renderCalendar(1)}
          </div>
          <div className="picker-actions">
            <button className="clear-button" onClick={handleClear}>
              Clear
            </button>
            <ButtonRound className="confirm-button" onClick={handleConfirm}>Confirm</ButtonRound>
          </div>
        </div>
      )}
    </div>
  );
};
